import React from "react";
import { Card, CardContent } from "../components/ui/Card";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-shuttlebee-dark to-shuttlebee-navy text-white py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold mb-2">Privacy Policy</h1>
          <p className="text-white/80">Last Updated: January 10, 2026</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        <Card>
          <CardContent className="pt-6 prose prose-gray max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Information We Collect</h2>
              <p className="text-gray-700 mb-4">
                We collect information that you provide directly to us when requesting transportation quotes and services:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Guardian Information:</strong> Name, email address, phone number</li>
                <li><strong>Student Information:</strong> Grade level, special needs requirements (car seats, booster seats, accessibility needs)</li>
                <li><strong>Transportation Details:</strong> Pickup and drop-off addresses, schedule preferences, selected weeks</li>
                <li><strong>Account Information:</strong> Email and password when you create an account</li>
                <li><strong>Communications:</strong> Messages and special requests you send to us</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. How We Use Your Information</h2>
              <p className="text-gray-700 mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Process and respond to your quote requests</li>
                <li>Provide transportation services to your children</li>
                <li>Communicate with you about services, schedules, and updates</li>
                <li>Maintain safety and security records</li>
                <li>Improve our services and customer experience</li>
                <li>Send marketing communications (only if you opt in)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Information Sharing</h2>
              <p className="text-gray-700 mb-4">
                We do not sell your personal information. We may share your information with:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Service Providers:</strong> Companies that help us operate our business (e.g., payment processors, email services)</li>
                <li><strong>Drivers:</strong> Assigned drivers receive necessary information to provide safe transportation</li>
                <li><strong>Schools and Camps:</strong> Destination facilities may receive arrival/departure information</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect safety</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Data Security</h2>
              <p className="text-gray-700 mb-4">
                We implement appropriate security measures to protect your personal information, including:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Encrypted data transmission (SSL/TLS)</li>
                <li>Secure cloud storage with access controls</li>
                <li>Regular security audits and updates</li>
                <li>Background-checked and trained staff</li>
              </ul>
              <p className="text-gray-700 mt-4">
                However, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Your Rights and Choices</h2>
              <p className="text-gray-700 mb-4">
                You have the right to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Access and review your personal information</li>
                <li>Request corrections to inaccurate information</li>
                <li>Request deletion of your account and data</li>
                <li>Opt out of marketing communications at any time</li>
                <li>Withdraw consent for data processing (where applicable)</li>
              </ul>
              <p className="text-gray-700 mt-4">
                To exercise these rights, contact us at <a href="mailto:hello@getshuttlebee.com" className="text-shuttlebee-dark hover:underline">hello@getshuttlebee.com</a>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Children's Privacy</h2>
              <p className="text-gray-700">
                Our services are intended for use by parents and guardians on behalf of children. We do not knowingly collect personal information directly from children under 13. All information about students is provided by parents/guardians.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Cookies and Tracking</h2>
              <p className="text-gray-700 mb-4">
                We use cookies and similar technologies to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Maintain your session when you're logged in</li>
                <li>Remember your preferences</li>
                <li>Analyze site usage and improve performance</li>
              </ul>
              <p className="text-gray-700 mt-4">
                You can control cookies through your browser settings.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Data Retention</h2>
              <p className="text-gray-700">
                We retain your personal information for as long as necessary to provide services and comply with legal obligations. Quote requests and service records are typically retained for 3 years after service completion.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Changes to This Policy</h2>
              <p className="text-gray-700">
                We may update this Privacy Policy from time to time. We will notify you of material changes by email or through a notice on our website. The "Last Updated" date at the top indicates when the policy was last revised.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-gray-50 rounded-lg p-4 text-gray-700">
                <p className="font-semibold mb-2">Shuttlebee - SCH Summerside Program</p>
                <p>Email: <a href="mailto:hello@getshuttlebee.com" className="text-shuttlebee-dark hover:underline">hello@getshuttlebee.com</a></p>
                <p>Phone: (484) 324-2559</p>
              </div>
            </section>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                By using our services, you acknowledge that you have read and understood this Privacy Policy.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
